import type { Task } from "../../types";
import type { SortOption, SortDirection } from "../../api/userSettings";

/**
 * Normalize a task priority to the 1–4 range used for sorting.
 *
 * A task's priority can be null/undefined (never set), 0, or out of range. The
 * priority badge renders any such value as "P4" (`priorityBadge[p] ?? [4]`), so
 * the sort MUST agree — otherwise `a.priority - b.priority` coerces null/0 to 0
 * and floats a "P4"-labelled task ABOVE P1 under ascending sort. PR #856 fixed
 * this in the backend comparator only; the frontend re-sort (which always runs
 * last and overrides the API order) still needed it.
 */
export function normalizePriority(
  priority: number | null | undefined,
): number {
  return priority && priority >= 1 && priority <= 4 ? priority : 4;
}

/**
 * Compare two tasks for the dashboard list sort. Pure so it can be unit-tested
 * independently of the Dashboard component's render tree.
 */
export function compareTasks(
  a: Task,
  b: Task,
  sortBy: SortOption,
  sortDirection: SortDirection,
): number {
  let comparison = 0;
  switch (sortBy) {
    case "due": {
      const aDate = a.due?.date ? new Date(a.due.date).getTime() : Infinity;
      const bDate = b.due?.date ? new Date(b.due.date).getTime() : Infinity;
      comparison = aDate - bDate;
      break;
    }
    case "priority":
      comparison = normalizePriority(a.priority) - normalizePriority(b.priority);
      break;
    case "created": {
      const aCreated = a.createdAt ? new Date(a.createdAt).getTime() : 0;
      const bCreated = b.createdAt ? new Date(b.createdAt).getTime() : 0;
      comparison = bCreated - aCreated;
      break;
    }
    case "title":
      comparison = a.content.localeCompare(b.content);
      break;
  }
  return sortDirection === "asc" ? comparison : -comparison;
}

/** Stable-ish sort of a task list using {@link compareTasks}. */
export function sortTasks(
  tasks: Task[],
  sortBy: SortOption,
  sortDirection: SortDirection,
): Task[] {
  return [...tasks].sort((a, b) => compareTasks(a, b, sortBy, sortDirection));
}
